import React, { useEffect, useRef, useState } from "react";
import { Collapse } from "react-bootstrap";
import { Link } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";

import CashCategoryServices from "../../../services/CashCategoryServices";
import CashbookServices from "../../../services/CashbookServices";
import CashEntryServices from "../../../services/CashEntryServices";
import CashReturnedEntriesPanel from "./CashReturnedEntriesPanel";
import CashRejectedEntriesPanel from "./CashRejectedEntriesPanel";

import CashOutEntryRow from "./CashOutEntryRow";
import {
  isAllowedCashDate,
  useCashEntryDateWindow
} from "./cashEntryWorkflowUtils";
import { CashDateAccessPanel } from "./CashEntryWorkflow";

const readData = (response) => {
  const body = response?.data?.status ? response.data : response;
  return body?.data;
};

const getAssignedStores = () => {
  try {
    const user = JSON.parse(localStorage.getItem("userDetails"));
    const stores = user?.data?.user?.store;
    return Array.isArray(stores) ? stores : [];
  } catch {
    return [];
  }
};

const toInitCap = (value = "") =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/\b[a-z]/g, (letter) => letter.toUpperCase());

const formatAmount = (value) => {
  const amount = Number(value);

  return (Number.isFinite(amount) ? amount : 0).toLocaleString(
    "en-IN",
    {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }
  );
};

const createRow = (id) => ({
  id,
  dateTime: "",
  categoryId: "",
  amount: "",
  paymentMode: "",
  vendorId: "",
  voucherNo: "",
  remark: "",
  attachments: []
});

const amountFields = [
  {
    key: "totalIn",
    label: "Total In",
    icon: "fa fa-arrow-down",
    className: "erp-cash-badge-in"
  },
  {
    key: "totalOut",
    label: "Total Out",
    icon: "fa fa-arrow-up",
    className: "erp-cash-badge-out"
  },
  {
    key: "balance",
    label: "Net Balance",
    icon: "fa fa-money",
    className: "erp-cash-badge-balance"
  }
];

const paymentModeOptions = [
  { value: "cash", label: "Cash" },
  { value: "online", label: "Online" }
];

const validateAttachments = (files) => {
  if (!files.length) return "";

  if (
    files.some(
      (file) => !(file instanceof File) || file.size === 0
    )
  ) {
    return "Please select valid, non-empty attachment files.";
  }

  const allowedTypes = [
    "image/jpeg",
    "image/png",
    "image/webp",
    "application/pdf"
  ];

  if (
    files.some(
      (file) => !allowedTypes.includes(file.type)
    )
  ) {
    return "Please select JPG, PNG, WEBP images or PDF files.";
  }

  return "";
};

const compressImage = (file) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    const cleanup = () => URL.revokeObjectURL(objectUrl);

    image.onerror = () => {
      cleanup();
      reject(new Error(`Unable to read image: ${file.name}`));
    };

    image.onload = () => {
      try {
        const scale = Math.min(1, 800 / image.naturalWidth);
        const canvas = document.createElement("canvas");

        canvas.width = Math.max(
          1,
          Math.round(image.naturalWidth * scale)
        );

        canvas.height = Math.max(
          1,
          Math.round(image.naturalHeight * scale)
        );

        const context = canvas.getContext("2d");

        if (!context) {
          throw new Error("Image compression is unavailable.");
        }

        context.fillStyle = "#ffffff";
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          (blob) => {
            cleanup();

            if (!blob) {
              reject(new Error(`Unable to compress: ${file.name}`));
              return;
            }

            if (blob.size >= file.size) {
              resolve(file);
              return;
            }

            const name = file.name.replace(/\.[^.]+$/, "");

            resolve(
              new File([blob], `${name}.jpg`, {
                type: "image/jpeg",
                lastModified: file.lastModified
              })
            );
          },
          "image/jpeg",
          0.7
        );
      } catch (error) {
        cleanup();
        reject(error);
      }
    };

    image.src = objectUrl;
  });

const CashOutForm = () => {
  const [open, setOpen] = useState(true);
  const [activeTab, setActiveTab] = useState("cashOut");
  const [stores] = useState(getAssignedStores);

  const [storeId, setStoreId] = useState(() =>
    String(
      stores.find((store) => store.status !== "inactive")?._id || ""
    )
  );

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState(() => [createRow(1)]);
  const [processingAttachments, setProcessingAttachments] =
    useState({});

  const [passbookState, setPassbookState] = useState({
    storeId: "",
    loading: false,
    passbook: null,
    error: false
  });

  const nextRowId = useRef(2);
  const submittingRef = useRef(false);
  const attachmentProcessingRef = useRef(new Set());

  useEffect(() => {
    let cancelled = false;

    const loadCategories = async () => {
      setLoading(true);

      try {
        const response =
          await CashCategoryServices.getCashCategories("active", "out");

        const data = readData(response);

        if (!Array.isArray(data?.categories)) {
          throw new Error("Unexpected category API response.");
        }

        if (!cancelled) setCategories(data.categories);
      } catch (error) {
        if (!cancelled) {
          Swal.fire(
            "Error",
            error?.response?.data?.message ||
            error?.message ||
            "Unable to load cash categories.",
            "error"
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadCategories();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!storeId) {
      setPassbookState({
        storeId: "",
        loading: false,
        passbook: null,
        error: false
      });
      return;
    }

    setPassbookState({
      storeId,
      loading: true,
      passbook: null,
      error: false
    });

    const loadPassbook = async () => {
      try {
        const response =
          await CashbookServices.getMyPassbookByStore(storeId);

        const data = readData(response);

        if (
          !data ||
          !Object.prototype.hasOwnProperty.call(data, "passbook")
        ) {
          throw new Error("Unexpected passbook API response.");
        }

        if (!cancelled) {
          setPassbookState({
            storeId,
            loading: false,
            passbook: data.passbook,
            error: false
          });
        }
      } catch (error) {
        if (!cancelled) {
          setPassbookState({
            storeId,
            loading: false,
            passbook: null,
            error: true
          });

          Swal.fire(
            "Error",
            error?.response?.data?.message ||
            error?.message ||
            "Unable to load passbook.",
            "error"
          );
        }
      }
    };

    loadPassbook();

    return () => {
      cancelled = true;
    };
  }, [storeId]);

  const storeOptions = stores.map((store) => ({
    value: String(store._id),
    label: [toInitCap(store.name), toInitCap(store.location)]
      .filter(Boolean)
      .join(", "),
    isDisabled: store.status === "inactive"
  }));

  const categoryOptions = categories.map((category) => ({
    value: String(category._id),
    label: category.name
  }));

  const currentPassbook =
    storeId &&
      passbookState.storeId === storeId &&
      !passbookState.loading &&
      !passbookState.error
      ? passbookState.passbook
      : null;

  const {
    dateWindow,
    dateWindowError,
    reloadDateWindow
  } = useCashEntryDateWindow(currentPassbook?._id);

  useEffect(() => {
    if (!dateWindow?.defaultDateTime) return;

    const defaultDateTime = dateWindow.defaultDateTime;

    setRows((previous) =>
      previous.map((row) =>
        row.dateTime
          ? row
          : { ...row, dateTime: defaultDateTime }
      )
    );
  }, [dateWindow?.defaultDateTime]);

  const entryDisabled =
    !currentPassbook || currentPassbook.status !== "active";

  const formDisabled = entryDisabled || saving;
  const attachmentsBusy =
    Object.values(processingAttachments).some(Boolean);

  const tabDisabled = saving || attachmentsBusy;

  let passbookTitle = "Select Store";

  if (storeId) {
    if (
      passbookState.storeId !== storeId ||
      passbookState.loading
    ) {
      passbookTitle = "Loading Passbook...";
    } else if (passbookState.error) {
      passbookTitle = "Unable to load passbook";
    } else if (currentPassbook) {
      passbookTitle = toInitCap(currentPassbook.name);
    } else {
      passbookTitle = "No active passbook found for this store";
    }
  }

  const handleAttachmentChange = async (rowId, fileList) => {
    if (
      formDisabled ||
      submittingRef.current ||
      attachmentProcessingRef.current.has(rowId)
    ) {
      return;
    }

    const files = Array.from(fileList || []);
    const validationError = validateAttachments(files);

    if (validationError) {
      await Swal.fire(
        "Invalid Attachments",
        validationError,
        "warning"
      );
      return;
    }

    attachmentProcessingRef.current.add(rowId);

    setProcessingAttachments((previous) => ({
      ...previous,
      [rowId]: true
    }));

    try {
      const preparedFiles = [];

      for (const file of files) {
        preparedFiles.push(
          file.type.startsWith("image/")
            ? await compressImage(file)
            : file
        );
      }

      if (
        preparedFiles.some(
          (file) => file.size > 5 * 1024 * 1024
        )
      ) {
        throw new Error(
          "Each attachment must be 5 MB or smaller after compression."
        );
      }

      setRows((previous) =>
        previous.map((row) =>
          row.id === rowId
            ? { ...row, attachments: preparedFiles }
            : row
        )
      );
    } catch (error) {
      await Swal.fire(
        "Attachment Error",
        error.message || "Unable to process attachments.",
        "error"
      );
    } finally {
      attachmentProcessingRef.current.delete(rowId);

      setProcessingAttachments((previous) => {
        const next = { ...previous };
        delete next[rowId];
        return next;
      });
    }
  };

  const updateRow = (id, field, value) => {
    if (formDisabled || submittingRef.current) return;

    setRows((previous) =>
      previous.map((row) =>
        row.id === id ? { ...row, [field]: value } : row
      )
    );
  };

  const getRowValidationErrors = (row) => {
    const errors = [];

    if (!dateWindow) {
      errors.push("Server date unavailable");
    } else if (!isAllowedCashDate(row.dateTime, dateWindow)) {
      errors.push(
        "Date & Time: select today/yesterday or an active Accounts-approved date"
      );
    }

    if (
      !categoryOptions.some(
        (option) => option.value === row.categoryId
      )
    ) {
      errors.push("Category");
    }

    const amount = Number(row.amount);

    if (
      String(row.amount ?? "").trim() === "" ||
      !Number.isFinite(amount) ||
      amount <= 0
    ) {
      errors.push("Amount greater than 0");
    }

    if (
      !paymentModeOptions.some(
        (option) => option.value === row.paymentMode
      )
    ) {
      errors.push("Payment Mode");
    }


    if (!String(row.remark || "").trim()) {
      errors.push("Remark");
    }


    return errors;
  };

  const invalidRowIndex = rows.findIndex(
    (row) => getRowValidationErrors(row).length > 0
  );

  const canSubmit =
    !formDisabled &&
    !attachmentsBusy &&
    !loading &&
    rows.length > 0 &&
    rows.length <= 100 &&
    invalidRowIndex === -1;

  const canAddRow = canSubmit && rows.length < 100;

  const addRow = () => {
    if (
      formDisabled ||
      submittingRef.current ||
      attachmentProcessingRef.current.size > 0 ||
      loading ||
      rows.length >= 100
    ) {
      return;
    }

    const currentInvalidIndex = rows.findIndex(
      (row) => getRowValidationErrors(row).length > 0
    );

    if (currentInvalidIndex !== -1) {
      Swal.fire(
        `Complete Row ${currentInvalidIndex + 1}`,
        `Please enter: ${getRowValidationErrors(
          rows[currentInvalidIndex]
        ).join(", ")}.`,
        "warning"
      );
      return;
    }

    const newRow = {
      ...createRow(nextRowId.current++),
      dateTime: dateWindow?.defaultDateTime || ""
    };

    setRows((previous) => [...previous, newRow]);
  };

  const removeRow = (id) => {
    if (
      formDisabled ||
      submittingRef.current ||
      attachmentProcessingRef.current.has(id)
    ) {
      return;
    }

    setRows((previous) =>
      previous.length > 1
        ? previous.filter((row) => row.id !== id)
        : previous
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (submittingRef.current) return;

    if (attachmentProcessingRef.current.size > 0) {
      await Swal.fire(
        "Processing Images",
        "Please wait until image compression finishes.",
        "info"
      );
      return;
    }

    if (entryDisabled || loading || !currentPassbook?._id) {
      await Swal.fire(
        "Passbook Required",
        "Please select a store with an active passbook.",
        "warning"
      );
      return;
    }

    if (!rows.length || rows.length > 100) {
      await Swal.fire(
        "Entry Rows",
        "Please submit between 1 and 100 rows.",
        "warning"
      );
      return;
    }

    const currentInvalidIndex = rows.findIndex(
      (row) => getRowValidationErrors(row).length > 0
    );

    if (currentInvalidIndex !== -1) {
      await Swal.fire(
        `Complete Row ${currentInvalidIndex + 1}`,
        `Please enter: ${getRowValidationErrors(
          rows[currentInvalidIndex]
        ).join(", ")}.`,
        "warning"
      );
      return;
    }

    for (let index = 0; index < rows.length; index++) {
      const files = rows[index].attachments || [];
      const attachmentError = validateAttachments(files);

      if (attachmentError) {
        await Swal.fire(
          `Row ${index + 1}: Attachments`,
          attachmentError,
          "warning"
        );
        return;
      }

      if (files.some((file) => file.size > 5 * 1024 * 1024)) {
        await Swal.fire(
          `Row ${index + 1}: Attachments`,
          "Each attachment must be 5 MB or smaller.",
          "warning"
        );
        return;
      }
    }

    submittingRef.current = true;
    setSaving(true);

    try {
      const preparedRows = [];

      for (let index = 0; index < rows.length; index++) {
        const row = rows[index];
        const files = row.attachments || [];
        let filenames = [];

        if (files.length > 0) {
          const response =
            await CashEntryServices.uploadAttachments(files);

          const uploadData = readData(response);

          if (
            !Array.isArray(uploadData?.filenames) ||
            uploadData.filenames.length !== files.length ||
            uploadData.filenames.some(
              (filename) =>
                typeof filename !== "string" ||
                !filename.trim()
            )
          ) {
            throw new Error(
              `Row ${index + 1}: Invalid attachment upload response.`
            );
          }

          filenames = uploadData.filenames;
        }

        preparedRows.push({
          dateTime: new Date(
            `${row.dateTime}:00+05:30`
          ).toISOString(),
          categoryId: row.categoryId,
          amount: Number(row.amount),
          paymentMode: row.paymentMode,
          vendorId: row.vendorId || null,
          voucherNo: String(row.voucherNo || "").trim(),
          remark: row.remark.trim(),
          image: filenames
        });
      }

      if (
        rows.some(
          (row) => !isAllowedCashDate(row.dateTime, dateWindow)
        )
      ) {
        throw new Error(
          "Date access has expired. Refresh date access and select an allowed date."
        );
      }

      await CashEntryServices.createCashEntries({
        passbookId: currentPassbook._id,
        entryType: "out",
        entries: preparedRows
      });

      setRows([
        {
          ...createRow(nextRowId.current++),
          dateTime: dateWindow?.defaultDateTime || ""
        }
      ]);

      reloadDateWindow();

      await Swal.fire({
        icon: "success",
        title: "Submitted",
        text: "Cash Out entries submitted for CashChecker approval.",
        confirmButtonText: "OK",
        allowOutsideClick: false,
        allowEscapeKey: false
      });
    } catch (error) {
      await Swal.fire(
        "Error",
        error?.response?.data?.message ||
        error?.message ||
        "Unable to submit Cash Out entries.",
        "error"
      );
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <div className="row erp-compact">
      <div className="col-xl-12">
        <div className="filter cm-content-box box-primary">
          <div className="content-title erp-cash-summary-header pt-3">
            <div className="cpa">
              <i
                className="fa fa-arrow-up me-2"
                aria-hidden="true"
              />
              Cash Out Entry
            </div>

            <div className="erp-cash-summary-middle">
              {amountFields.map((field) => (
                <div
                  key={field.key}
                  className={`erp-cash-badge ${field.className}`}
                >
                  <i className={field.icon} aria-hidden="true" />

                  <span>
                    <span className="erp-cash-badge-label">
                      {field.label}
                    </span>

                    <span className="erp-cash-badge-value">
                      {formatAmount(currentPassbook?.[field.key])}
                    </span>
                  </span>
                </div>
              ))}
            </div>

            <div className="tools erp-cash-tools">
              <Link
                to="#"
                className={`SlideToolHeader ${open ? "collapse" : "expand"
                  }`}
                onClick={(event) => {
                  event.preventDefault();
                  setOpen((previous) => !previous);
                }}
                aria-label="Toggle cash out form"
                aria-expanded={open}
              >
                <i
                  className="fas fa-angle-up"
                  aria-hidden="true"
                />
              </Link>
            </div>
          </div>

          <div className="row justify-content-center px-3 pt-2 mb-2 pb-2">
            <div className="col-12 col-sm-8 col-md-5 col-lg-4 col-xl-3">
              <label
                htmlFor="cash-out-store"
                className="form-label"
              >
                Store / Location
              </label>

              <Select
                inputId="cash-out-store"
                options={storeOptions}
                value={
                  storeOptions.find(
                    (option) => option.value === storeId
                  ) || null
                }
                onChange={(selected) => {
                  if (
                    !submittingRef.current &&
                    attachmentProcessingRef.current.size === 0
                  ) {
                    setStoreId(selected?.value || "");
                  }
                }}
                placeholder="Select Store / Location"
                isSearchable
                isClearable
                isDisabled={tabDisabled}
                noOptionsMessage={() => "No assigned stores found"}
                className="erp-select"
                classNamePrefix="erp-select"
                menuPlacement="auto"
              />
            </div>
          </div>

          <Collapse in={open}>
            <div className="cm-content-body form excerpt">
              <div className="card-body">
                <div
                  className="nav nav-tabs mb-3"
                  role="tablist"
                  aria-label="Cash Out requests"
                >
                  <button
                    id="cash-out-request-tab"
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "cashOut"}
                    aria-controls="cash-out-request-panel"
                    className={`nav-link px-4 py-2 ${activeTab === "cashOut" ? "active" : ""
                      }`}
                    disabled={tabDisabled}
                    onClick={() => setActiveTab("cashOut")}
                  >
                    <i
                      className="bi bi-cash-stack me-2"
                      aria-hidden="true"
                    />
                    Cash Out Request
                  </button>

                  <button
                    id="cash-out-date-access-tab"
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "dateAccess"}
                    aria-controls="cash-out-date-access-panel"
                    className={`nav-link px-4 py-2 ${activeTab === "dateAccess" ? "active" : ""
                      }`}
                    disabled={tabDisabled}
                    onClick={() => setActiveTab("dateAccess")}
                  >
                    <i
                      className="bi bi-calendar-check me-2"
                      aria-hidden="true"
                    />
                    Date Access Request
                  </button>
                  <button
                    id="cash-out-returned-tab"
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "returned"}
                    aria-controls="cash-out-returned-panel"
                    className={`nav-link px-4 py-2 ${activeTab === "returned" ? "active" : ""
                      }`}
                    disabled={tabDisabled}
                    onClick={() => setActiveTab("returned")}
                  >
                    Returned Cash Out
                  </button>

                  <button
                    id="cash-out-rejected-tab"
                    type="button"
                    role="tab"
                    aria-selected={activeTab === "rejected"}
                    aria-controls="cash-out-rejected-panel"
                    className={`nav-link px-4 py-2 ${activeTab === "rejected" ? "active" : ""
                      }`}
                    disabled={tabDisabled}
                    onClick={() => setActiveTab("rejected")}
                  >
                    <svg
                      width="17"
                      height="17"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="me-2"
                      style={{ verticalAlign: "middle" }}
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="9" />
                      <path d="m9 9 6 6M15 9l-6 6" />
                    </svg>
                    Rejected Cash Out
                  </button>
                </div>

                <div className="row g-2 mb-3">
                  <div className="form-group col-12">
                    <label
                      id="cash-out-passbook-label"
                      className="form-label"
                    >
                      Passbook Name
                    </label>

                    <div
                      className="form-control erp-readonly erp-passbook-name"
                      role="status"
                      aria-live="polite"
                      aria-labelledby="cash-out-passbook-label"
                      title={passbookTitle}
                    >
                      <i
                        className="fa fa-book me-2 text-primary"
                        aria-hidden="true"
                      />
                      <span>{passbookTitle}</span>
                    </div>
                  </div>
                </div>

                <div
                  id="cash-out-request-panel"
                  role="tabpanel"
                  aria-labelledby="cash-out-request-tab"
                  hidden={activeTab !== "cashOut"}
                >
                  <form onSubmit={handleSubmit}>
                    {dateWindowError && (
                      <div
                        className="alert alert-danger py-2"
                        role="alert"
                      >
                        {dateWindowError}
                      </div>
                    )}

                    <fieldset disabled={formDisabled}>
                      {rows.map((row) => (
                        <CashOutEntryRow
                          key={row.id}
                          row={row}
                          dateWindow={dateWindow}
                          disabled={formDisabled}
                          categoriesLoading={loading}
                          categoryOptions={categoryOptions}
                          paymentModeOptions={paymentModeOptions}
                          updateRow={updateRow}
                          onRemoveRow={() => removeRow(row.id)}
                          canRemoveRow={rows.length > 1}
                          attachmentsLoading={Boolean(
                            processingAttachments[row.id]
                          )}
                          onAttachmentsChange={(files) =>
                            handleAttachmentChange(row.id, files)
                          }
                        />
                      ))}

                      <div className="d-flex flex-wrap gap-2 mt-2">
                        <button
                          type="button"
                          className="btn btn-primary erp-small-button"
                          onClick={addRow}
                          disabled={!canAddRow}
                        >
                          <i
                            className="fa fa-plus me-2"
                            aria-hidden="true"
                          />
                          Add Row
                        </button>

                        <button
                          type="submit"
                          className="btn btn-success erp-small-button"
                          disabled={!canSubmit}
                        >
                          {saving ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                aria-hidden="true"
                              />
                              Submitting...
                            </>
                          ) : (
                            <>
                              <i
                                className="fa fa-check me-2"
                                aria-hidden="true"
                              />
                              Submit
                            </>
                          )}
                        </button>
                      </div>

                      {!entryDisabled &&
                        !loading &&
                        invalidRowIndex !== -1 && (
                          <div
                            className="small text-muted mt-2"
                            role="status"
                            aria-live="polite"
                          >
                            Complete Row {invalidRowIndex + 1}:{" "}
                            {getRowValidationErrors(
                              rows[invalidRowIndex]
                            ).join(", ")}.
                          </div>
                        )}
                    </fieldset>
                  </form>
                </div>

                <div
                  id="cash-out-date-access-panel"
                  role="tabpanel"
                  aria-labelledby="cash-out-date-access-tab"
                  hidden={activeTab !== "dateAccess"}
                >
                  {activeTab === "dateAccess" && (
                    currentPassbook?._id ? (
                      <div
                        className="p-3 p-md-4"
                        style={{
                          border: "1px solid #e5e9f0",
                          borderRadius: 12,
                          background: "#ffffff"
                        }}
                      >
                        <CashDateAccessPanel
                          key={currentPassbook._id}
                          embedded
                          passbookId={currentPassbook._id}
                          disabled={formDisabled}
                          onRefresh={reloadDateWindow}
                        />
                      </div>
                    ) : (
                      <div className="alert alert-info mb-0">
                        Select a Store / Location with an active
                        passbook to request date access.
                      </div>
                    )
                  )}
                </div>
                <div
                  id="cash-out-returned-panel"
                  role="tabpanel"
                  aria-labelledby="cash-out-returned-tab"
                  hidden={activeTab !== "returned"}
                >
                  {activeTab === "returned" && (
                    <CashReturnedEntriesPanel
                      key={`returned-${storeId}-${currentPassbook?._id || ""}`}
                      storeId={storeId}
                      passbookId={currentPassbook?._id}
                      entryType="out"
                    />
                  )}
                </div>

                <div
                  id="cash-out-rejected-panel"
                  role="tabpanel"
                  aria-labelledby="cash-out-rejected-tab"
                  hidden={activeTab !== "rejected"}
                >
                  {activeTab === "rejected" && (
                    <CashRejectedEntriesPanel
                      key={`rejected-${storeId}-${currentPassbook?._id || ""}`}
                      storeId={storeId}
                      passbookId={currentPassbook?._id}
                      entryType="out"
                    />
                  )}
                </div>
              </div>
            </div>
          </Collapse>
        </div>
      </div>
    </div>
  );
};

export default CashOutForm;